import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import FileManager from '@/components/portal/FileManager';
import { Permissions } from '@/components/portal/usePermissions';

interface ProductionSectionProps {
  eventId: string;
  permissions: Permissions;
}

interface AccordionItemProps {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function AccordionItem({ title, icon, children, defaultOpen = false }: AccordionItemProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-zinc-900 rounded-lg border border-zinc-800 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-6 hover:bg-zinc-800/50 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          {icon}
          <h2 className="text-xl font-semibold text-white">{title}</h2>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-zinc-400 transition-transform flex-shrink-0 ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className="px-6 pb-6">
          {children}
        </div>
      )}
    </div>
  );
}

export default function ProductionSection({ eventId, permissions }: ProductionSectionProps) {

  return (
    <div className="space-y-4">
      <AccordionItem
        title="Renderings / Images"
        defaultOpen={false}
      >
        <FileManager
          eventId={eventId}
          section="renderings"
          title=""
          acceptedFileTypes="image/*"
          showImageCarousel={true}
          canEdit={permissions.canEdit}
        />
      </AccordionItem>

      <AccordionItem title="Advance Docs">
        <FileManager
          eventId={eventId}
          section="advance_docs"
          title=""
          canEdit={permissions.canEdit}
        />
      </AccordionItem>

      <AccordionItem title="Technical Drawings">
        <FileManager
          eventId={eventId}
          section="technical_drawings"
          title=""
          canEdit={permissions.canEdit}
        />
      </AccordionItem>

      <AccordionItem title="Production Files">
        <FileManager
          eventId={eventId}
          section="production_files"
          title=""
          canEdit={permissions.canEdit}
        />
      </AccordionItem>

      <AccordionItem title="Venue Files">
        <FileManager
          eventId={eventId}
          section="venue_files"
          title=""
          canEdit={permissions.canEdit}
        />
      </AccordionItem>

      <AccordionItem title="Venue CADs">
        <FileManager
          eventId={eventId}
          section="venue_cads"
          title=""
          canEdit={permissions.canEdit}
        />
      </AccordionItem>

      <AccordionItem title="Notes">
        <FileManager
          eventId={eventId}
          section="notes"
          title=""
          canEdit={permissions.canEdit}
        />
      </AccordionItem>
    </div>
  );
}
